/**
 * MortgageMonitorPage — the refi radar on the funded Arive book.
 *
 * Two tabs: Opportunities (stat cards, per-loan-type Today's Rates, and a
 * sortable/filterable opportunity table with hover detail cards and actions)
 * and Rate Alerts (self-serve alert email management). Admins get an LO
 * scope dropdown plus an Admin drawer with rates/scan/backfill controls and
 * the full Mortgage Monitor settings panel.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Container,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Switch,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  FiActivity,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDatabase,
  FiDollarSign,
  FiExternalLink,
  FiFilter,
  FiMoreVertical,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiSettings,
  FiTrendingDown,
  FiXCircle,
  FiZap,
} from 'react-icons/fi';
import { LineChart, Line, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useHistory } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';

import { db } from '../../utils/init-firebase';
import { useAuth } from '../../contexts/AuthContext';
import { groupCRMUsersByDTC, sortCRMUsersWithDTCPriority } from '../../utils/crmUserSorting';
import { useMortgageMonitor } from '../../hooks/useMortgageMonitor';
import { buildAriveDeepLink } from '../../constants/ariveConstants';
import { useAgentPage } from '../../components/FutureBot/FutureBotProvider';
import { useTourActions } from '../../components/FeatureTour/FeatureTourProvider';
import { useMortgageMonitorAgentTools } from './agent/useMortgageMonitorAgentTools';
import RefiMonitorSettingsManager from '../../components/CRM/Settings/RefiMonitorSettingsManager';
import ManualRatesEditor from './MortgageMonitor/ManualRatesEditor';
import RateAlertsTab from './MortgageMonitor/RateAlertsTab';
import {
  DEFAULT_MONITOR_FILTERS,
  borrowerName,
  fmtLoanType,
  matchesMonitorFilters,
  sortOpportunities,
} from './MortgageMonitor/monitorFilters';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const fmtMoney = (n) => {
  const num = Number(n) || 0;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (Math.abs(num) >= 1e3) return `$${Math.round(num / 1e3)}K`;
  return `$${Math.round(num)}`;
};

const fmtMoneyFull = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

const fmtRate = (r) => (r === null || r === undefined ? '—' : `${Number(r).toFixed(3).replace(/0+$/, '').replace(/\.$/, '')}%`);

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(`${String(d).slice(0, 10)}T12:00:00Z`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// loan_term is stored in months; whole years read better (360 -> "30 yr").
const fmtTerm = (months) => {
  const m = Number(months);
  if (!(m > 0)) return '—';
  return m % 12 === 0 ? `${m / 12} yr` : `${m} mo`;
};

const STATUS_META = {
  open: { label: 'In the Money', color: 'green' },
  contacted: { label: 'Contacted', color: 'blue' },
  enrolled: { label: 'Enrolled', color: 'purple' },
  dismissed: { label: 'Dismissed', color: 'gray' },
  below_threshold: { label: 'Below Threshold', color: 'orange' },
};

const STATUS_TABS = ['open', 'contacted', 'enrolled', 'dismissed', 'below_threshold'];

const PRODUCT_LABELS = {
  '30yr_fixed': 'Conventional 30yr',
  '15yr_fixed': '15yr Fixed',
  fha_30yr: 'FHA 30yr',
  va_30yr: 'VA 30yr',
  usda_30yr: 'USDA 30yr',
  nonqm_30yr: 'Non-QM 30yr',
};

// Rate cards row: conventional + the three most common non-conventional types.
const RATE_CARDS = [
  { product: '30yr_fixed', label: 'Conventional 30yr Fixed' },
  { product: 'va_30yr', label: 'VA 30yr' },
  { product: 'fha_30yr', label: 'FHA 30yr' },
  { product: 'nonqm_30yr', label: 'Non-QM 30yr' },
];

// ---------------------------------------------------------------------------
// Rate trend sparkline + rate card
// ---------------------------------------------------------------------------

function RateTrend({ series, color, label }) {
  const data = (series || []).map((p) => ({ date: p.date, rate: p.rate }));
  if (data.length < 2) return <Box h="48px" />;
  return (
    <Box h="48px" w="100%">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
          <YAxis domain={['dataMin - 0.05', 'dataMax + 0.05']} hide />
          <RechartsTooltip
            formatter={(value) => [`${value}%`, label]}
            labelFormatter={(l, payload) => payload?.[0]?.payload?.date || ''}
          />
          <Line type="monotone" dataKey="rate" stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

function RateCard({ label, series, colors }) {
  const current = series.length ? series[series.length - 1] : null;
  const prev = series.length > 1 ? series[series.length - 2] : null;
  const direction = current && prev
    ? (current.rate === prev.rate ? 'flat' : current.rate < prev.rate ? 'down' : 'up')
    : null;
  return (
    <Box bg={colors.cardBg} border="1px solid" borderColor={colors.cardBorder} borderRadius="lg" p={4}>
      <Stat>
        <StatLabel>{label}</StatLabel>
        <HStack align="baseline" spacing={2}>
          <StatNumber>{current ? fmtRate(current.rate) : '—'}</StatNumber>
          {direction && (
            <Text fontSize="sm" color={direction === 'up' ? 'red.400' : colors.greenText}>
              {direction} vs last week
            </Text>
          )}
        </HStack>
        <RateTrend series={series} color={colors.trendStroke} label={label} />
      </Stat>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Hover detail card (Tooltip label content)
// ---------------------------------------------------------------------------

function DetailItem({ label, value, sub, colors, valueProps = {} }) {
  return (
    <Box>
      <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wide" color={colors.subtleText} fontWeight="700">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="600" {...valueProps}>{value}</Text>
      {sub && <Text fontSize="xs" color={colors.subtleText}>{sub}</Text>}
    </Box>
  );
}

function RowDetailCard({ opp, colors }) {
  const name = borrowerName(opp);
  const status = STATUS_META[opp.status] || STATUS_META.open;
  const marketLabel = PRODUCT_LABELS[opp.market_rate_product] || opp.market_rate_product || 'market';
  const location = [opp.property_city, opp.property_state].filter(Boolean).join(', ');
  return (
    <Box p={4} minW={{ base: '300px', md: '420px' }}>
      {/* Header: who + status + score */}
      <Flex justify="space-between" align="flex-start" gap={3}>
        <Box>
          <Text fontWeight="800" fontSize="md">{name}</Text>
          <Text fontSize="xs" color={colors.subtleText}>
            {[opp.borrower_email, opp.borrower_phone].filter(Boolean).join(' · ') || 'No contact info'}
          </Text>
          {location && <Text fontSize="xs" color={colors.subtleText}>{location}</Text>}
        </Box>
        <VStack spacing={1} align="flex-end">
          <Badge colorScheme={status.color}>{status.label}</Badge>
          <Badge colorScheme={opp.score >= 200 ? 'red' : opp.score >= 100 ? 'orange' : 'gray'}>
            Score {Math.round(opp.score)}
          </Badge>
          {opp.opportunity_type !== 'rate' && <Badge colorScheme="cyan">EQUITY-RICH</Badge>}
        </VStack>
      </Flex>

      <Divider my={3} />

      {/* Loan snapshot */}
      <SimpleGrid columns={2} spacingX={6} spacingY={3}>
        <DetailItem colors={colors} label="Loan"
          value={`${fmtLoanType(opp.mortgage_type)} · ${fmtTerm(opp.loan_term)}`} />
        <DetailItem colors={colors} label="Funded" value={fmtDate(opp.funded_date)} />
        <DetailItem colors={colors} label="Original Amount" value={fmtMoneyFull(opp.base_loan_amount)} />
        <DetailItem colors={colors} label="Est. Balance Today" value={fmtMoneyFull(opp.est_current_balance)} />
      </SimpleGrid>

      <Divider my={3} />

      {/* Rate math */}
      <SimpleGrid columns={3} spacingX={4} spacingY={3}>
        <DetailItem colors={colors} label="Their Rate" value={fmtRate(opp.note_rate)} valueProps={{ color: 'red.400' }} />
        <DetailItem colors={colors} label={marketLabel} value={fmtRate(opp.market_rate)} valueProps={{ color: colors.greenText }} />
        <DetailItem colors={colors} label="Delta" value={`${opp.rate_delta_bps} bps`} />
      </SimpleGrid>

      <Divider my={3} />

      {/* Savings + equity */}
      <SimpleGrid columns={2} spacingX={6} spacingY={3}>
        <DetailItem colors={colors} label="Est. Monthly Savings"
          value={opp.est_monthly_savings ? `$${Math.round(opp.est_monthly_savings)}/mo` : '—'}
          sub={`payment ${fmtMoneyFull(opp.est_current_payment)} → ${fmtMoneyFull(opp.est_new_payment)}`}
          valueProps={{ color: colors.greenText }} />
        <DetailItem colors={colors} label="Est. Equity"
          value={opp.est_equity !== null && opp.est_equity !== undefined
            ? `${fmtMoney(opp.est_equity)} (${Number(opp.est_equity_pct || 0).toFixed(0)}%)`
            : '—'}
          sub={opp.est_property_value ? `est. value ${fmtMoneyFull(opp.est_property_value)}` : undefined} />
      </SimpleGrid>

      <Divider my={3} />

      {/* Footer: LO + timeline */}
      <Flex justify="space-between" fontSize="xs" color={colors.subtleText} wrap="wrap" gap={2}>
        <Text>LO: {opp.owner_name || (opp.owner_email || '').split('@')[0] || 'Unassigned'}</Text>
        <Text>Flagged {fmtDate(opp.first_detected_at)} · Checked {fmtDate(opp.last_evaluated_at)}</Text>
      </Flex>
      <Text fontSize="2xs" color={colors.subtleText} mt={2}>
        Click the borrower name to open the deal in a new tab.
      </Text>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MortgageMonitorPage() {
  const toast = useToast();
  const history = useHistory();
  const { currentUser, Roles, SuperAdmin } = useAuth();

  const monitor = useMortgageMonitor();
  const {
    data, loading, error, ownerFilter, setOwnerFilter,
    refresh, act, actioningId, adminBusy, refreshRates, setManualRates, runScan, runBackfill,
  } = monitor;

  const [pageTab, setPageTab] = useState(0); // 0 = Opportunities, 1 = Rate Alerts
  const [statusTab, setStatusTab] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_MONITOR_FILTERS);
  const [sortKey, setSortKey] = useState('score');
  const [sortDir, setSortDir] = useState('desc');
  const [enrollTarget, setEnrollTarget] = useState(null);
  const [backfillDryRun, setBackfillDryRun] = useState(true);
  const [backfillLimit, setBackfillLimit] = useState(150);
  const [backfillResult, setBackfillResult] = useState(null);

  const enrollModal = useDisclosure();
  const adminDrawer = useDisclosure();

  // Hoisted color tokens (Rules of Hooks — never inline in JSX).
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const trendStroke = useColorModeValue('#2B6CB0', '#63B3ED');
  const tableHeadBg = useColorModeValue('gray.50', 'gray.900');
  const rowHover = useColorModeValue('gray.50', 'gray.700');
  const greenText = useColorModeValue('green.600', 'green.300');
  const detailText = useColorModeValue('gray.800', 'gray.100');
  const linkColor = useColorModeValue('blue.600', 'blue.300');

  const colors = useMemo(
    () => ({ cardBg, cardBorder, subtleText, greenText, trendStroke }),
    [cardBg, cardBorder, subtleText, greenText, trendStroke]
  );

  const isAdmin = SuperAdmin || Roles?.includes('CRM Admin') || currentUser?.CRMRole === 'CRM Admin';

  // Admin view-as: same grouped CRM-user selector as Dashboard / My Deals.
  // It scopes the Opportunities tab (server-side via ownerUid) AND the Rate
  // Alerts tab (manage that LO's alert settings).
  const [crmUsers, setCrmUsers] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    const loadCrmUsers = async () => {
      try {
        const usersQuery = query(collection(db, 'users'), where('CRMUser', '==', true));
        const snapshot = await getDocs(usersQuery);
        const users = snapshot.docs.map((d) => ({
          uid: d.id,
          name: d.data().Name || d.data().name || d.data().email,
          ...d.data(),
        }));
        setCrmUsers(sortCRMUsersWithDTCPriority(users));
      } catch (err) {
        console.error('Mortgage Monitor: CRM users load failed', err);
      }
    };
    loadCrmUsers();
  }, [isAdmin]);

  // Selecting yourself is just "my own view" — no impersonation semantics.
  const viewAsUid = isAdmin && ownerFilter !== 'all' && ownerFilter !== currentUser?.uid ? ownerFilter : null;
  const viewAsUser = useMemo(
    () => (viewAsUid ? crmUsers.find((u) => u.uid === viewAsUid) || null : null),
    [viewAsUid, crmUsers]
  );
  const viewAsName = viewAsUser?.name
    || (viewAsUid ? (data?.owners || []).find((o) => o.ownerUid === viewAsUid)?.ownerName : null)
    || null;

  // FutureBot registration (hooks rule: before any early return).
  const { registration: agentRegistration } = useMortgageMonitorAgentTools({
    monitor, statusTab, setStatusTab, search, setSearch, isAdmin,
    filters, setFilters, sortKey, setSortKey, sortDir, setSortDir,
  });
  useAgentPage(agentRegistration);

  // FeatureTour: named actions the Mortgage Monitor tours can invoke.
  useTourActions(useMemo(() => {
    const clickTab = (id) => document.querySelector(`[data-tour="${id}"]`)?.click();
    return {
      showOppsTab: () => clickTab('monitor-tab-opps'),
      showAlertsTab: () => clickTab('monitor-tab-alerts'),
      openMonitorAdmin: adminDrawer.onOpen,
    };
  }, [adminDrawer.onOpen]));

  const opportunities = useMemo(() => data?.opportunities || [], [data]);
  const stats = data?.stats || {};
  const settings = data?.settings || {};
  const rateTrend = data?.rateTrend || {};
  const trend30 = useMemo(() => rateTrend['30yr_fixed'] || [], [rateTrend]);
  const current30 = trend30.length ? trend30[trend30.length - 1] : null;

  const activeStatus = STATUS_TABS[statusTab];

  // Loan-type filter options come from the data itself (display labels).
  const loanTypeOptions = useMemo(() => {
    const set = new Set();
    for (const o of opportunities) if (o.mortgage_type) set.add(fmtLoanType(o.mortgage_type));
    return [...set].sort();
  }, [opportunities]);

  const termOptions = useMemo(() => {
    const set = new Set();
    for (const o of opportunities) {
      const years = Math.round((Number(o.loan_term) || 0) / 12);
      if (years > 0) set.add(years);
    }
    return [...set].sort((a, b) => b - a);
  }, [opportunities]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = opportunities
      .filter((o) => o.status === activeStatus)
      .filter((o) => matchesMonitorFilters(o, filters))
      .filter((o) => {
        if (!q) return true;
        const hay = [
          o.borrower_first_name, o.borrower_last_name, o.borrower_email,
          o.owner_email, o.owner_name, o.property_city, o.property_state, o.mortgage_type,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    return sortOpportunities(rows, sortKey, sortDir);
  }, [opportunities, activeStatus, search, filters, sortKey, sortDir]);

  const statusCounts = useMemo(() => {
    const counts = {};
    for (const s of STATUS_TABS) counts[s] = 0;
    for (const o of opportunities) {
      if (counts[o.status] !== undefined) counts[o.status] += 1;
    }
    return counts;
  }, [opportunities]);

  // Active-filter chips.
  const filterChips = useMemo(() => {
    const chips = [];
    for (const t of filters.loanTypes || []) chips.push({ key: `type:${t}`, label: t });
    if (filters.termYears != null) chips.push({ key: 'termYears', label: `${filters.termYears} yr term` });
    if (filters.minSavings != null) chips.push({ key: 'minSavings', label: `≥ $${filters.minSavings}/mo` });
    if (filters.minBalance != null) chips.push({ key: 'minBalance', label: `balance ≥ ${fmtMoney(filters.minBalance)}` });
    if (filters.maxBalance != null) chips.push({ key: 'maxBalance', label: `balance ≤ ${fmtMoney(filters.maxBalance)}` });
    if (filters.minEquityPct != null) chips.push({ key: 'minEquityPct', label: `equity ≥ ${filters.minEquityPct}%` });
    if (filters.minScore != null) chips.push({ key: 'minScore', label: `score ≥ ${filters.minScore}` });
    return chips;
  }, [filters]);

  const removeChip = useCallback((key) => {
    setFilters((prev) => {
      if (key.startsWith('type:')) {
        const t = key.slice(5);
        return { ...prev, loanTypes: (prev.loanTypes || []).filter((x) => x !== t) };
      }
      return { ...prev, [key]: null };
    });
  }, []);

  const handleSort = useCallback((key) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      // Text columns start A→Z, numeric metrics start high→low.
      setSortDir(['borrower', 'mortgage_type', 'owner', 'funded_date'].includes(key) ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const numberFilter = (key) => (str, num) => {
    setFilters((prev) => ({ ...prev, [key]: str === '' || !Number.isFinite(num) ? null : num }));
  };

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const openDealInNewTab = useCallback((opp) => {
    const url = opp.deal_id
      ? `/crm/deals/${opp.deal_id}`
      : opp.contact_id
        ? `/crm/contacts/${opp.contact_id}`
        : null;
    if (url) window.open(url, '_blank', 'noopener');
  }, []);

  const handleAction = useCallback(async (opp, action) => {
    const result = await act(opp.id, action);
    if (result.success) {
      const messages = {
        dismiss: 'Opportunity dismissed — the scan will not reopen it.',
        reopen: 'Opportunity reopened.',
        mark_contacted: 'Marked as contacted.',
        create_task: 'Follow-up task created (due tomorrow).',
      };
      toast({ title: messages[action] || 'Done', status: 'success', duration: 3000, isClosable: true });
    } else {
      toast({ title: 'Action failed', description: result.error, status: 'error', duration: 6000, isClosable: true });
    }
  }, [act, toast]);

  const handleEnrollConfirm = useCallback(async () => {
    if (!enrollTarget) return;
    const result = await act(enrollTarget.id, 'enroll');
    enrollModal.onClose();
    setEnrollTarget(null);
    if (result.success) {
      toast({
        title: 'Borrower enrolled',
        description: `Tagged "${result.tagName}"${result.contactCreated ? ' (new contact created)' : ''} — the Refi Opportunity Nurture campaign will pick them up.`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } else {
      toast({ title: 'Enroll failed', description: result.error, status: 'error', duration: 8000, isClosable: true });
    }
  }, [act, enrollTarget, enrollModal, toast]);

  const handleRefreshRates = useCallback(async () => {
    const result = await refreshRates();
    if (result.success) {
      toast({ title: 'Market rates refreshed', description: `Upserted ${result.upserted ?? '—'} observations (FRED + derived per-type rates).`, status: 'success', duration: 4000, isClosable: true });
    } else {
      toast({ title: 'Rate refresh failed', description: result.error, status: 'error', duration: 6000, isClosable: true });
    }
  }, [refreshRates, toast]);

  const handleRunScan = useCallback(async () => {
    const result = await runScan();
    if (result.success) {
      toast({
        title: 'Scan complete',
        description: `${result.scanned ?? '—'} loans evaluated, ${result.qualified ?? '—'} in the money (${result.inserted ?? 0} new).`,
        status: 'success', duration: 5000, isClosable: true,
      });
    } else {
      toast({ title: 'Scan failed', description: result.error, status: 'error', duration: 6000, isClosable: true });
    }
  }, [runScan, toast]);

  const handleBackfill = useCallback(async () => {
    setBackfillResult(null);
    const result = await runBackfill({ dryRun: backfillDryRun, limit: backfillLimit });
    setBackfillResult(result);
    if (!result.success) {
      toast({ title: 'Backfill failed', description: result.error, status: 'error', duration: 8000, isClosable: true });
    }
  }, [runBackfill, backfillDryRun, backfillLimit, toast]);

  // Manual rate entry: apply hand-typed rates (they hold until the next fetch),
  // then automatically re-scan so the book is scored against the new rates.
  const handleApplyManualRates = useCallback(async (rates) => {
    const result = await setManualRates(rates);
    if (!result.success) {
      toast({ title: 'Manual rate save failed', description: result.error, status: 'error', duration: 6000, isClosable: true });
      return result;
    }
    toast({
      title: 'Manual rates applied — re-scanning the book',
      description: 'They hold until the next rate fetch. Scoring every loan against the new rates now…',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    const scan = await runScan();
    if (scan.success) {
      toast({
        title: 'Scan complete',
        description: `${scan.scanned ?? '—'} loans evaluated, ${scan.qualified ?? '—'} in the money (${scan.inserted ?? 0} new).`,
        status: 'success', duration: 5000, isClosable: true,
      });
    } else {
      toast({ title: 'Scan failed', description: `Manual rates were saved, but the re-scan failed: ${scan.error}. Use "Re-scan the book now" to retry.`, status: 'error', duration: 8000, isClosable: true });
    }
    return result;
  }, [setManualRates, runScan, toast]);

  // After settings save: re-derive rates (spreads/overrides) and reload.
  const handleSettingsSaved = useCallback(async () => {
    await refreshRates();
  }, [refreshRates]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const renderDelta = (opp) => {
    const bps = Number(opp.rate_delta_bps) || 0;
    return (
      <VStack spacing={0} align="flex-start">
        <HStack spacing={1}>
          <Text fontWeight="600">{fmtRate(opp.note_rate)}</Text>
          <Icon as={FiTrendingDown} color={greenText} boxSize={3.5} />
          <Text color={subtleText}>{fmtRate(opp.market_rate)}</Text>
        </HStack>
        <Badge colorScheme={bps >= 100 ? 'green' : 'teal'} fontSize="0.65rem">
          {bps} bps above market
        </Badge>
      </VStack>
    );
  };

  const columns = useMemo(() => {
    const base = [
      { key: 'borrower', label: 'Borrower' },
      { key: 'funded_date', label: 'Funded' },
      { key: 'mortgage_type', label: 'Loan Type' },
      { key: 'loan_term', label: 'Term' },
      { key: 'rate_delta_bps', label: 'Rate vs Market' },
      { key: 'est_current_balance', label: 'Est. Balance', numeric: true },
      { key: 'est_monthly_savings', label: 'Savings / mo', numeric: true },
      { key: 'est_equity_pct', label: 'Equity', numeric: true },
      { key: 'score', label: 'Score', numeric: true },
    ];
    if (isAdmin && ownerFilter === 'all') base.push({ key: 'owner', label: 'LO' });
    return base;
  }, [isAdmin, ownerFilter]);

  return (
    <Box bg={pageBg} minH="100vh" pb={12}>
      <Container maxW="1500px" pt={6}>
        {/* Header */}
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4} mb={4}>
          <Box data-tour="monitor-header">
            <HStack spacing={3}>
              <Icon as={FiActivity} boxSize={7} color="blue.400" />
              <Heading size="lg">Mortgage Monitor</Heading>
            </HStack>
            <Text color={subtleText} mt={1}>
              Funded clients who are in the money vs today&apos;s market — call them before another lender does.
            </Text>
          </Box>

          <HStack spacing={3}>
            {isAdmin && (
              <Select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                maxW="260px"
                size="sm"
                borderRadius="md"
                data-tour="monitor-view-as"
              >
                <option value="all">All Users</option>
                {(() => {
                  // Same grouped selector as Dashboard / My Deals; counts show
                  // who has opportunities. Falls back to opportunity owners
                  // until the directory loads.
                  const countByUid = new Map((data?.owners || []).map((o) => [o.ownerUid, o.count]));
                  if (!crmUsers.length) {
                    return (data?.owners || []).map((o) => (
                      <option key={o.ownerUid} value={o.ownerUid}>
                        {o.ownerName || o.ownerEmail || o.ownerUid} ({o.count})
                      </option>
                    ));
                  }
                  const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                  const renderOption = (user) => {
                    const count = countByUid.get(user.uid);
                    return (
                      <option key={user.uid} value={user.uid}>
                        {user.name}{count ? ` (${count})` : ''}
                      </option>
                    );
                  };
                  return (
                    <>
                      {dtcUsers.length > 0 && (
                        <optgroup label="DTC Loan Officers">
                          {dtcUsers.map(renderOption)}
                        </optgroup>
                      )}
                      <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                        {otherUsers.map(renderOption)}
                      </optgroup>
                    </>
                  );
                })()}
              </Select>
            )}
            <Tooltip label="Refresh">
              <IconButton
                aria-label="Refresh"
                icon={<FiRefreshCw />}
                size="sm"
                variant="outline"
                isLoading={loading}
                onClick={() => refresh()}
              />
            </Tooltip>
            {isAdmin && (
              <Button size="sm" leftIcon={<FiSettings />} variant="outline" onClick={adminDrawer.onOpen} data-tour="monitor-admin-btn">
                Admin
              </Button>
            )}
          </HStack>
        </Flex>

        {error && (
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {/* Page tabs: Opportunities | Rate Alerts */}
        <Tabs index={pageTab} onChange={setPageTab} colorScheme="blue" variant="line">
          <TabList mb={5}>
            <Tab fontWeight="600" data-tour="monitor-tab-opps"><Icon as={FiActivity} mr={2} />Opportunities</Tab>
            <Tab fontWeight="600" data-tour="monitor-tab-alerts"><Icon as={FiSend} mr={2} />Rate Alerts</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} pt={0}>
              {/* Stat cards */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4} data-tour="monitor-stats">
                <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={4}>
                  <Stat>
                    <StatLabel>Clients in the Money</StatLabel>
                    <Skeleton isLoaded={!loading} borderRadius="md">
                      <StatNumber>{stats.openCount ?? 0}</StatNumber>
                    </Skeleton>
                    <StatHelpText mb={0}>{stats.contactedCount ?? 0} contacted · {stats.enrolledCount ?? 0} enrolled</StatHelpText>
                  </Stat>
                </Box>
                <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={4}>
                  <Stat>
                    <StatLabel>Est. Client Savings</StatLabel>
                    <Skeleton isLoaded={!loading} borderRadius="md">
                      <StatNumber color={greenText}>{fmtMoney(stats.totalMonthlySavings)}/mo</StatNumber>
                    </Skeleton>
                    <StatHelpText mb={0}>across open + contacted opportunities</StatHelpText>
                  </Stat>
                </Box>
                <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={4}>
                  <Stat>
                    <StatLabel>Est. LO Comp</StatLabel>
                    <Skeleton isLoaded={!loading} borderRadius="md">
                      <StatNumber color={greenText}>{fmtMoney(stats.estLoComp)}</StatNumber>
                    </Skeleton>
                    <StatHelpText mb={0}>
                      if every in-the-money client refinanced · {fmtMoney(stats.totalBalance)} balance × {((stats.loCompBps ?? 150) / 100).toFixed(2)}%
                    </StatHelpText>
                  </Stat>
                </Box>
              </SimpleGrid>

              {/* Today's Rates row */}
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mb={6} data-tour="monitor-rates">
                {RATE_CARDS.map((card) => (
                  <RateCard
                    key={card.product}
                    label={card.label}
                    series={rateTrend[card.product] || []}
                    colors={colors}
                  />
                ))}
              </SimpleGrid>

              {/* Table card */}
              <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" overflow="hidden" data-tour="monitor-table">
                <Flex px={4} pt={3} pb={0} justify="space-between" align="center" wrap="wrap" gap={3}>
                  <Tabs index={statusTab} onChange={setStatusTab} variant="soft-rounded" colorScheme="blue" size="sm">
                    <TabList data-tour="monitor-status-tabs">
                      {STATUS_TABS.map((s) => (
                        <Tab key={s} fontSize="sm">
                          {STATUS_META[s].label} ({statusCounts[s] ?? 0})
                        </Tab>
                      ))}
                    </TabList>
                  </Tabs>
                  <HStack spacing={2}>
                    <Popover placement="bottom-end" closeOnBlur>
                      <PopoverTrigger>
                        <Button size="sm" variant="outline" leftIcon={<FiFilter />} data-tour="monitor-filters">
                          Filters{filterChips.length > 0 ? ` (${filterChips.length})` : ''}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent w="340px">
                        <PopoverHeader fontWeight="600" fontSize="sm">Filter opportunities</PopoverHeader>
                        <PopoverBody>
                          <VStack align="stretch" spacing={4}>
                            <FormControl>
                              <FormLabel fontSize="xs" mb={1}>Loan types</FormLabel>
                              <CheckboxGroup
                                value={filters.loanTypes}
                                onChange={(vals) => setFilters((prev) => ({ ...prev, loanTypes: vals }))}
                              >
                                <Wrap spacing={3}>
                                  {loanTypeOptions.map((t) => (
                                    <WrapItem key={t}>
                                      <Checkbox value={t} size="sm">{t}</Checkbox>
                                    </WrapItem>
                                  ))}
                                </Wrap>
                              </CheckboxGroup>
                            </FormControl>

                            <HStack spacing={3} align="flex-end">
                              <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Term</FormLabel>
                                <Select
                                  size="sm"
                                  value={filters.termYears ?? ''}
                                  onChange={(e) => setFilters((prev) => ({
                                    ...prev,
                                    termYears: e.target.value === '' ? null : Number(e.target.value),
                                  }))}
                                >
                                  <option value="">Any</option>
                                  {termOptions.map((y) => <option key={y} value={y}>{y} yr</option>)}
                                </Select>
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Min savings / mo</FormLabel>
                                <NumberInput size="sm" min={0} value={filters.minSavings ?? ''} onChange={numberFilter('minSavings')}>
                                  <NumberInputField placeholder="$" />
                                </NumberInput>
                              </FormControl>
                            </HStack>

                            <HStack spacing={3}>
                              <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Min balance</FormLabel>
                                <NumberInput size="sm" min={0} step={10000} value={filters.minBalance ?? ''} onChange={numberFilter('minBalance')}>
                                  <NumberInputField placeholder="$" />
                                </NumberInput>
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Max balance</FormLabel>
                                <NumberInput size="sm" min={0} step={10000} value={filters.maxBalance ?? ''} onChange={numberFilter('maxBalance')}>
                                  <NumberInputField placeholder="$" />
                                </NumberInput>
                              </FormControl>
                            </HStack>

                            <HStack spacing={3}>
                              <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Min equity %</FormLabel>
                                <NumberInput size="sm" min={0} max={100} value={filters.minEquityPct ?? ''} onChange={numberFilter('minEquityPct')}>
                                  <NumberInputField placeholder="%" />
                                </NumberInput>
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Min score</FormLabel>
                                <NumberInput size="sm" min={0} value={filters.minScore ?? ''} onChange={numberFilter('minScore')}>
                                  <NumberInputField placeholder="0" />
                                </NumberInput>
                              </FormControl>
                            </HStack>

                            <Button size="xs" variant="ghost" alignSelf="flex-start"
                              onClick={() => setFilters(DEFAULT_MONITOR_FILTERS)}
                              isDisabled={filterChips.length === 0}>
                              Clear all filters
                            </Button>
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                    <InputGroup maxW="240px" size="sm" data-tour="monitor-search">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={FiSearch} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search borrower, city, LO…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        borderRadius="md"
                      />
                    </InputGroup>
                  </HStack>
                </Flex>

                {filterChips.length > 0 && (
                  <Wrap px={4} pt={2} spacing={2}>
                    {filterChips.map((chip) => (
                      <WrapItem key={chip.key}>
                        <Tag size="sm" colorScheme="blue" borderRadius="full">
                          <TagLabel>{chip.label}</TagLabel>
                          <TagCloseButton onClick={() => removeChip(chip.key)} />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}

                {loading && !data ? (
                  <Flex justify="center" py={16}><Spinner size="lg" /></Flex>
                ) : filtered.length === 0 ? (
                  <VStack py={14} spacing={2}>
                    <Icon as={FiCheckCircle} boxSize={8} color="gray.400" />
                    <Text color={subtleText}>
                      {filterChips.length > 0 || search
                        ? 'Nothing matches the current filters or search.'
                        : activeStatus === 'open'
                          ? 'No open opportunities right now — the daily scan will surface new ones as rates move.'
                          : `No ${STATUS_META[activeStatus].label.toLowerCase()} opportunities.`}
                    </Text>
                    {isAdmin && activeStatus === 'open' && filterChips.length === 0 && !search &&
                      (statusCounts.open + statusCounts.contacted + statusCounts.enrolled + statusCounts.dismissed + statusCounts.below_threshold) === 0 && (
                      <Button size="sm" mt={2} leftIcon={<FiDatabase />} onClick={adminDrawer.onOpen}>
                        Run the Arive backfill to populate the funded book
                      </Button>
                    )}
                  </VStack>
                ) : (
                  <Box overflowX="auto">
                    <Table size="sm">
                      <Thead bg={tableHeadBg}>
                        <Tr>
                          {columns.map((col) => {
                            const active = sortKey === col.key;
                            return (
                              <Th
                                key={col.key}
                                isNumeric={col.numeric}
                                cursor="pointer"
                                userSelect="none"
                                onClick={() => handleSort(col.key)}
                                aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                _hover={{ color: 'blue.400' }}
                              >
                                <HStack spacing={0.5} display="inline-flex">
                                  <Text as="span">{col.label}</Text>
                                  {active && <Icon as={sortDir === 'asc' ? FiChevronUp : FiChevronDown} boxSize={3.5} />}
                                </HStack>
                              </Th>
                            );
                          })}
                          <Th w="48px" />
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filtered.map((opp) => {
                          const name = borrowerName(opp);
                          const ariveLink = buildAriveDeepLink('loan', opp.arive_loan_id);
                          const busy = actioningId === opp.id;
                          const hasLink = !!(opp.deal_id || opp.contact_id);
                          return (
                            <Tooltip
                              key={opp.id}
                              label={<RowDetailCard opp={opp} colors={colors} />}
                              placement="auto"
                              openDelay={450}
                              hasArrow
                              bg={cardBg}
                              color={detailText}
                              border="1px solid"
                              borderColor={cardBorder}
                              borderRadius="lg"
                              boxShadow="xl"
                              p={0}
                              maxW="460px"
                            >
                              <Tr _hover={{ bg: rowHover }}>
                                <Td>
                                  <VStack spacing={0} align="flex-start">
                                    <HStack spacing={1.5}>
                                      <Text
                                        fontWeight="600"
                                        color={hasLink ? linkColor : undefined}
                                        cursor={hasLink ? 'pointer' : 'default'}
                                        _hover={hasLink ? { textDecoration: 'underline' } : undefined}
                                        onClick={() => openDealInNewTab(opp)}
                                      >
                                        {name}
                                      </Text>
                                      {opp.opportunity_type !== 'rate' && (
                                        <Badge colorScheme="cyan" fontSize="0.6rem">EQUITY</Badge>
                                      )}
                                    </HStack>
                                    <Text fontSize="xs" color={subtleText}>{opp.borrower_email || opp.borrower_phone || '—'}</Text>
                                  </VStack>
                                </Td>
                                <Td>
                                  <VStack spacing={0} align="flex-start">
                                    <Text>{fmtDate(opp.funded_date)}</Text>
                                    <Text fontSize="xs" color={subtleText}>
                                      {[opp.property_city, opp.property_state].filter(Boolean).join(', ')}
                                    </Text>
                                  </VStack>
                                </Td>
                                <Td>
                                  <Text>{fmtLoanType(opp.mortgage_type)}</Text>
                                </Td>
                                <Td>
                                  <Text>{fmtTerm(opp.loan_term)}</Text>
                                </Td>
                                <Td>{renderDelta(opp)}</Td>
                                <Td isNumeric>
                                  <Text>{fmtMoney(opp.est_current_balance)}</Text>
                                </Td>
                                <Td isNumeric>
                                  <Text fontWeight="700" color={greenText}>
                                    {opp.est_monthly_savings ? `$${Math.round(opp.est_monthly_savings)}` : '—'}
                                  </Text>
                                </Td>
                                <Td isNumeric>
                                  {opp.est_equity_pct ? `${Number(opp.est_equity_pct).toFixed(0)}%` : '—'}
                                </Td>
                                <Td isNumeric>
                                  <Badge colorScheme={opp.score >= 200 ? 'red' : opp.score >= 100 ? 'orange' : 'gray'}>
                                    {Math.round(opp.score)}
                                  </Badge>
                                </Td>
                                {isAdmin && ownerFilter === 'all' && (
                                  <Td>
                                    <Text fontSize="xs" color={subtleText}>
                                      {opp.owner_name || (opp.owner_email || '').split('@')[0] || '—'}
                                    </Text>
                                  </Td>
                                )}
                                <Td>
                                  <Menu placement="bottom-end" isLazy>
                                    <MenuButton
                                      as={IconButton}
                                      aria-label="Actions"
                                      icon={busy ? <Spinner size="xs" /> : <FiMoreVertical />}
                                      size="xs"
                                      variant="ghost"
                                      isDisabled={busy}
                                    />
                                    <MenuList fontSize="sm">
                                      {(opp.status === 'open' || opp.status === 'contacted') && (
                                        <MenuItem
                                          icon={<FiZap />}
                                          onClick={() => { setEnrollTarget(opp); enrollModal.onOpen(); }}
                                        >
                                          Enroll in refi nurture…
                                        </MenuItem>
                                      )}
                                      {opp.status === 'open' && (
                                        <MenuItem icon={<FiSend />} onClick={() => handleAction(opp, 'mark_contacted')}>
                                          Mark contacted
                                        </MenuItem>
                                      )}
                                      <MenuItem icon={<FiClock />} onClick={() => handleAction(opp, 'create_task')}>
                                        Create follow-up task
                                      </MenuItem>
                                      {opp.deal_id && (
                                        <MenuItem icon={<FiExternalLink />} onClick={() => openDealInNewTab(opp)}>
                                          Open deal in new tab
                                        </MenuItem>
                                      )}
                                      {opp.contact_id && (
                                        <MenuItem icon={<FiExternalLink />} onClick={() => history.push(`/crm/contacts/${opp.contact_id}`)}>
                                          View contact in CRM
                                        </MenuItem>
                                      )}
                                      {ariveLink && (
                                        <MenuItem icon={<FiExternalLink />} onClick={() => window.open(ariveLink, '_blank')}>
                                          Open in Arive
                                        </MenuItem>
                                      )}
                                      <MenuDivider />
                                      {opp.status === 'dismissed' ? (
                                        <MenuItem icon={<FiRefreshCw />} onClick={() => handleAction(opp, 'reopen')}>
                                          Reopen
                                        </MenuItem>
                                      ) : (
                                        <MenuItem icon={<FiXCircle />} color="red.400" onClick={() => handleAction(opp, 'dismiss')}>
                                          Dismiss
                                        </MenuItem>
                                      )}
                                    </MenuList>
                                  </Menu>
                                </Td>
                              </Tr>
                            </Tooltip>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                <Flex px={4} py={2.5} borderTop="1px solid" borderColor={cardBorder} justify="space-between" align="center">
                  <Text fontSize="xs" color={subtleText}>
                    Thresholds: ≥{settings.minRateDeltaBps ?? 50} bps · ≥${settings.minMonthlySavings ?? 50}/mo · ≥{settings.minMonthsSeasoned ?? 6} mo seasoning · equity flag ≥{settings.equityFlagPct ?? 20}%
                  </Text>
                  <Text fontSize="xs" color={subtleText}>
                    {filtered.length} of {opportunities.length} opportunities
                  </Text>
                </Flex>
              </Box>
            </TabPanel>

            {/* Rate Alerts tab (admin view-as follows the user selector) */}
            <TabPanel px={0} pt={0}>
              <RateAlertsTab viewAsUid={viewAsUid} viewAsName={viewAsName} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      {/* Enroll confirm modal */}
      <Modal isOpen={enrollModal.isOpen} onClose={() => { enrollModal.onClose(); setEnrollTarget(null); }} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enroll in refi nurture?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {enrollTarget && (
              <VStack align="stretch" spacing={3}>
                <Text>
                  <strong>
                    {[enrollTarget.borrower_first_name, enrollTarget.borrower_last_name].filter(Boolean).join(' ') || enrollTarget.borrower_email}
                  </strong>{' '}
                  is ~{enrollTarget.rate_delta_bps} bps above market — est. savings{' '}
                  <strong>${Math.round(enrollTarget.est_monthly_savings || 0)}/mo</strong>.
                </Text>
                <Text fontSize="sm" color={subtleText}>
                  This tags the borrower&apos;s contact with{' '}
                  <Badge colorScheme="purple">{settings.tagName || 'Refi Opportunity'}</Badge>{' '}
                  {enrollTarget.contact_id ? '' : '(creating the contact in your book first) '}
                  and starts the email-first Refi Opportunity Nurture campaign as you.
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => { enrollModal.onClose(); setEnrollTarget(null); }}>
              Cancel
            </Button>
            <Button colorScheme="purple" leftIcon={<FiZap />} onClick={handleEnrollConfirm} isLoading={actioningId === enrollTarget?.id}>
              Enroll borrower
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Admin drawer: controls + full settings */}
      <Drawer isOpen={adminDrawer.isOpen} onClose={adminDrawer.onClose} size="lg" placement="right">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Mortgage Monitor Admin</DrawerHeader>
          <DrawerBody pb={8}>
            <VStack align="stretch" spacing={5}>
              <HStack spacing={3}>
                <Button
                  size="sm"
                  leftIcon={<FiDollarSign />}
                  onClick={handleRefreshRates}
                  isLoading={adminBusy === 'rates'}
                  isDisabled={!!adminBusy && adminBusy !== 'rates'}
                  data-tour="monitor-admin-rates"
                >
                  Fetch market rates now
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FiRefreshCw />}
                  onClick={handleRunScan}
                  isLoading={adminBusy === 'scan'}
                  isDisabled={!!adminBusy && adminBusy !== 'scan'}
                  data-tour="monitor-admin-scan"
                >
                  Re-scan the book now
                </Button>
              </HStack>

              {/* Manual rate entry: fields show current rates; typed values hold until next fetch */}
              <ManualRatesEditor
                rateTrend={rateTrend}
                onApply={handleApplyManualRates}
                isBusy={adminBusy === 'manualRates'}
              />

              <Box borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={4} data-tour="monitor-admin-backfill">
                <Heading size="xs" mb={1}>Arive detail backfill</Heading>
                <Text fontSize="sm" color={subtleText} mb={3}>
                  Discovers funded loans missing from the mirror and enriches thin rows with note rate,
                  funded date, and key dates. Run with dry-run first; then run for real repeatedly until
                  remaining hits 0.
                </Text>
                <HStack spacing={4} mb={3}>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <FormLabel htmlFor="backfill-dryrun" mb={0} fontSize="sm">Dry run</FormLabel>
                    <Switch id="backfill-dryrun" isChecked={backfillDryRun} onChange={(e) => setBackfillDryRun(e.target.checked)} />
                  </FormControl>
                  <FormControl w="140px">
                    <NumberInput size="sm" value={backfillLimit} min={10} max={400} onChange={(_, v) => setBackfillLimit(Number.isFinite(v) ? v : 150)}>
                      <NumberInputField />
                    </NumberInput>
                    <FormHelperText fontSize="xs" mt={1}>Detail fetches per run</FormHelperText>
                  </FormControl>
                  <Button
                    size="sm"
                    colorScheme={backfillDryRun ? 'gray' : 'blue'}
                    leftIcon={<FiDatabase />}
                    onClick={handleBackfill}
                    isLoading={adminBusy === 'backfill'}
                    isDisabled={!!adminBusy && adminBusy !== 'backfill'}
                  >
                    {backfillDryRun ? 'Preview backfill' : 'Run backfill'}
                  </Button>
                </HStack>
                {backfillResult?.success && (
                  <Alert status={backfillResult.dryRun ? 'info' : 'success'} borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    <Box>
                      {backfillResult.dryRun ? (
                        <Text>
                          Arive company book: {backfillResult.discovery?.ariveTotal ?? '?'} loans · mirror holds{' '}
                          {backfillResult.mirrorLoanCount ?? '?'} · {backfillResult.remaining ?? 0} funded rows still missing
                          detail. Turn off dry run to start the real backfill.
                        </Text>
                      ) : (
                        <>
                          <Text>
                            Discovery saw {backfillResult.discovery?.rowsSeen ?? 0} loans; enrichment fetched{' '}
                            {backfillResult.enrichment?.enriched ?? 0} of {backfillResult.enrichment?.attempted ?? 0} attempted
                            {backfillResult.enrichment?.failed ? ` (${backfillResult.enrichment.failed} failed)` : ''}
                            {backfillResult.enrichment?.rateLimited ? ' — stopped early on Arive rate limit' : ''}.
                          </Text>
                          <Text fontWeight="600">
                            Remaining thin rows: {backfillResult.remaining ?? '—'}
                            {(backfillResult.remaining ?? 0) > 0 ? ' — run again (dry run off) to continue.' : ' — book is fully enriched.'}
                          </Text>
                        </>
                      )}
                    </Box>
                  </Alert>
                )}
              </Box>

              <Divider />

              {/* Full settings panel — thresholds, LO comp, per-type rates */}
              <Box data-tour="monitor-admin-settings">
                <RefiMonitorSettingsManager
                  conventionalRate={current30 ? current30.rate : null}
                  onSaved={handleSettingsSaved}
                />
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

export default MortgageMonitorPage;
